import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { IconButton, InputAdornment, Typography } from '@mui/material';
import { Stack } from '@mui/system';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';

import LoginFormField from '../types/loginFormField.enum';
import LoginFormValues from '../types/loginFormValues.type';
import RememberMePopover from './RememberMePopover';

type FormProps = {
  formValues: LoginFormValues;
  formValuesErrors: Partial<LoginFormValues>;
  setFormValues: (value: LoginFormValues) => void;
  setFormValuesErrors: (value: Partial<LoginFormValues>) => void;
};

const Form: FC<FormProps> = ({
  formValues,
  formValuesErrors,
  setFormValues,
  setFormValuesErrors,
}) => {
  const { t } = useTranslation(['login', 'forms', 'common']);
  const [isPasswordVisible, setPasswordVisible] = useState(false);

  const onValidate = (field: Partial<LoginFormValues>) => {
    const newValues = { ...formValues, ...field };
    const newErrors = { ...formValuesErrors };

    Object.keys(field).forEach((key) => {
      if (key === 'accountId') {
        !newValues.accountId
          ? (newErrors.accountId = t('login:validation.accountId'))
          : (newErrors.accountId = '');
      }

      if (key === 'email') {
        if (!newValues.email) {
          newErrors.email = t('login:validation.emailRequired');
        } else if (!/^\S+@\S+\.\S+$/.test(newValues.email)) {
          newErrors.email = t('login:validation.email');
        } else {
          newErrors.email = '';
        }
      }

      if (key === 'password') {
        !newValues.password
          ? (newErrors.password = t('login:validation.password'))
          : (newErrors.password = '');
      }
    });

    setFormValuesErrors(newErrors);
  };

  return (
    <WaveUIForm_V2
      fields={[
        {
          field_type: FieldType.Text,
          field_props: {
            name: LoginFormField.AccountId,
            label: t('login:accountId'),
            required: true,
            placeholder: t('login:enterAccountId'),
            value: formValues.accountId,
            endAdornment: (
              <InputAdornment position='end'>
                <AccountBoxOutlinedIcon
                  sx={{ width: 22, height: 24, color: 'text.secondary' }}
                />
              </InputAdornment>
            ),
            error: !!formValuesErrors.accountId,
            helperText: formValuesErrors.accountId,
            onChange: (e) => {
              const value = e.target.value;
              const newValue = value.replace(/[^0-9]/g, '');

              setFormValues({ ...formValues, accountId: newValue });
              onValidate({ accountId: newValue });
            },
          },
        },
        {
          field_type: FieldType.Checkbox,
          field_props: {
            name: LoginFormField.RememberMe,
            checked: formValues.rememberMe,
            label: (
              <Stack
                sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}
              >
                <Typography>{t('login:rememberMe')}</Typography>
                <RememberMePopover />
              </Stack>
            ),
          },
        },
        {
          field_type: FieldType.Text,
          field_props: {
            name: LoginFormField.Email,
            label: t('login:email'),
            type: 'text',
            required: true,
            placeholder: t('login:enterEmail'),
            inputProps: {
              autoComplete: 'username',
            },
            endAdornment: (
              <InputAdornment position='end'>
                <MailOutlineIcon
                  sx={{ width: 24, height: 22, color: 'text.secondary' }}
                />
              </InputAdornment>
            ),
            error: !!formValuesErrors.email,
            helperText: formValuesErrors.email,
          },
        },
        {
          field_type: FieldType.Password,
          field_props: {
            name: LoginFormField.Password,
            label: t('login:password'),
            type: isPasswordVisible ? 'text' : 'password',
            required: true,
            placeholder: t('login:enterPassword'),
            inputProps: {
              autoComplete: 'current-password',
            },
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  onClick={() => setPasswordVisible(!isPasswordVisible)}
                  edge='end'
                >
                  {isPasswordVisible ? (
                    <VisibilityIcon
                      sx={{ width: 24, height: 24, color: 'text.secondary' }}
                    />
                  ) : (
                    <VisibilityOffIcon
                      sx={{ width: 24, height: 24, color: 'text.secondary' }}
                    />
                  )}
                </IconButton>
              </InputAdornment>
            ),
            error: !!formValuesErrors.password,
            helperText: formValuesErrors.password,
          },
        },
      ]}
      getFieldsValues={(fieldsValues) => {
        const newValue = fieldsValues as Partial<LoginFormValues>;

        setFormValues({ ...formValues, ...newValue });
        onValidate(newValue);
      }}
    />
  );
};

export default Form;
